import React from 'react';
import { Calendar, CheckCircle2, AlertTriangle } from 'lucide-react';
import { BookingSettingsResponseDto, CalendarConnectionResponse } from '../../types/booking';
import Spinner from '../Spinner';

interface Props {
  settings?: BookingSettingsResponseDto | null;
  connection?: CalendarConnectionResponse | null;
  loading?: boolean;
}

export default function CalendarSyncStatus({ settings, connection, loading }: Props) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 p-6">
        <Spinner size="h-6 w-6" />
        <span className="text-sm text-strivre-mutedtext">Loading calendar sync status...</span>
      </div>
    );
  }

  const provider = connection?.provider ?? settings?.connectedCalendarProvider ?? 'Calendar';
  const email = connection?.email ?? settings?.connectedCalendarEmail ?? 'No email available';
  const isConnected = connection?.connected || settings?.isCalendarConnected;

  if (!isConnected) {
    return (
      <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <AlertTriangle size={16} />
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900">⚠ No Calendar Connected</p>
            <p className="text-sm text-amber-800">Connect Google or Microsoft Calendar to enable automatic booking synchronization.</p>
          </div>
        </div>
      </div>
    );
  }

  const isGoogle = provider === 'Google';
  const badgeClasses = isGoogle
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-blue-200 bg-strivre-lightburgundy text-blue-700';

  return (
    <div className="space-y-3 rounded-xl border border-strivre-lightnavy bg-slate-50 p-4">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg border ${badgeClasses}`}>
          {isGoogle ? <Calendar size={16} /> : <CheckCircle2 size={16} />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-strivre-navy">✅ Connected to {provider} Calendar</p>
          <p className="text-sm text-strivre-mutedtext break-all">{email}</p>
          <p className="mt-2 text-sm text-strivre-mutedtext">Calendar Sync: {settings?.calendarSyncEnabled ? 'Enabled' : 'Disabled'}</p>
        </div>
      </div>
    </div>
  );
}

